import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CAccordion, CAccordionItem, CAccordionHeader, CAccordionBody,
  CCard, CCardHeader, CCardBody, CCardFooter,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody,
  CFormSelect, CButton, CSpinner, CAlert
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPlus, cilTrash, cilX } from '@coreui/icons';
import { apiClient } from 'services/Api';
import ToastNotification from 'components/globals/ToastNotification';
import { jwtDecode } from 'jwt-decode';

// ===== helpers =====
const labelOf = (u) => `${(u.first_name || '')} ${(u.last_name || '')}`.trim();
const keyFor = (evaluatorId, evaluateeId) => `${evaluatorId}::${evaluateeId}`;

export default function EvaluationLink() {
  const { id } = useParams();
  const deptId = Number(id);

  // data
  const [members, setMembers] = useState([]);        // dept users
  const [activeUsers, setActiveUsers] = useState([]); // for managers list
  const [links, setLinks] = useState([]);            // existing department links

  // ui/state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  // auth
  const token = localStorage.getItem('access_token');
  const decodedToken = token ? jwtDecode(token) : {};
  const is_manager = !!decodedToken?.is_manager;
  const is_staff = !!decodedToken?.is_staff;
  const canEdit = is_manager || is_staff;

  // authoritative state (ONLY this is submitted):
  // Map<evaluateeId, number[] of evaluatorIds>
  const [evaluatedByRows, setEvaluatedByRows] = useState(new Map());

  // ===== Loaders =====
  const loadMembers = async () => {
    const res = await apiClient.get(`/departments/${deptId}/memberships/`);
    const list = (res.data || []).map((m) => {
      const u = m.user || {};
      return { id: u.id, first_name: u.first_name, last_name: u.last_name, is_manager: !!u.is_manager };
    });
    setMembers(list);
  };

  const loadActiveUsers = async () => {
    const res = await apiClient.get('/users/active/');
    setActiveUsers(res.data || []);
  };

  const loadLinks = async () => {
    const res = await apiClient.get(`/department-evaluation-links/for-department/?department_id=${deptId}`);
    setLinks(res.data || []);
  };

  // Build evaluatedByRows from members+links
  const rebuildEvaluatedByFromLinks = (mList, lList) => {
    const map = new Map();
    for (const m of mList) map.set(m.id, []);
    for (const l of lList) {
      const ev = Number(l.evaluator);
      const ee = Number(l.evaluatee);
      if (map.has(ee)) {
        map.get(ee).push(ev);
      }
    }
    setEvaluatedByRows(map);
  };

  // Initial load
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        await Promise.all([loadMembers(), loadActiveUsers()]);
        if (cancel) return;
        await loadLinks();
      } catch (e) {
        if (!cancel) {
          setToast({
            title: 'Relações de Avaliação',
            message: 'Erro a carregar dados.',
            type: 'error',
          });
        }
        console.error(e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [deptId]);

  // When members/links change, hydrate the evaluatedBy map
  useEffect(() => {
    if (members.length >= 0) {
      rebuildEvaluatedByFromLinks(members, links);
    }
  }, [members, links]);

  // ===== Options =====
  const memberOptions = useMemo(() => {
    return [...members].sort((a, b) =>
      `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`)
    );
  }, [members]);

  // who can evaluate a user: department members + active managers
  const evaluatorPool = useMemo(() => {
    const byId = new Map();
    for (const u of members) byId.set(u.id, u);
    for (const u of activeUsers) if (u.is_manager) byId.set(u.id, { ...u, is_manager: true });
    return Array.from(byId.values()).sort((a, b) =>
      `${a.first_name || ''} ${a.last_name || ''}`.localeCompare(`${b.first_name || ''} ${b.last_name || ''}`)
    );
  }, [members, activeUsers]);

  // who a user can evaluate: only department members
  const evaluateePool = memberOptions;

  // ===== Derived (view-only) "Avalia" map from authoritative evaluatedByRows =====
  // Map<evaluatorId, number[] of evaluateeIds>
  const evaluatesDerived = useMemo(() => {
    const derived = new Map();
    for (const m of members) derived.set(m.id, []);
    for (const [evaluateeId, evalList] of evaluatedByRows.entries()) {
      for (const evaluatorId of (evalList || [])) {
        if (evaluatorId && evaluatorId !== evaluateeId) {
          const arr = derived.get(evaluatorId) || [];
          arr.push(evaluateeId);
          derived.set(evaluatorId, arr);
        }
      }
    }
    return derived;
  }, [evaluatedByRows, members]);

  // ===== UI helpers (authoritative table) =====
  const addRowEvalBy = (evaluateeId) =>
    setEvaluatedByRows((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(evaluateeId) || [])];
      arr.push(''); // empty slot
      next.set(evaluateeId, arr);
      return next;
    });

  const removeRowEvalBy = (evaluateeId, idx) =>
    setEvaluatedByRows((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(evaluateeId) || [])];
      arr.splice(idx, 1);
      next.set(evaluateeId, arr);
      return next;
    });

  const clearEvalBy = (evaluateeId) =>
    setEvaluatedByRows((prev) => {
      const next = new Map(prev);
      next.set(evaluateeId, []);
      return next;
    });

  const changeEvalBy = (evaluateeId, idx, value) =>
    setEvaluatedByRows((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(evaluateeId) || [])];
      arr[idx] = value ? Number(value) : '';
      // prevent self
      if (arr[idx] === evaluateeId) arr[idx] = '';
      next.set(evaluateeId, arr);
      return next;
    });

  // Ensure no duplicate evaluator selections in a single evaluatee table
  const evaluatorOptionsFor = (evaluateeId, currentVal) => {
    const selected = new Set((evaluatedByRows.get(evaluateeId) || []).filter(Boolean));
    // allow the currently selected value to remain
    if (currentVal) selected.delete(currentVal);
    return evaluatorPool
      .filter((x) => x.id !== evaluateeId)
      .filter((x) => !selected.has(x.id));
  };

  // ===== Save (diff) — ONLY from evaluatedByRows =====
  const onSave = async () => {
    if (!canEdit) {
      setToast({
        title: 'Relações de Avaliação',
        message: 'Sem permissões para gravar alterações.',
        type: 'error',
      });
      return;
    }
    setSaving(true);
    try {
      const desired = new Set();
      const pairs = [];

      for (const [evaluateeId, arr] of evaluatedByRows.entries()) {
        for (const evaluatorId of arr.filter(Boolean)) {
          const evaluator = Number(evaluatorId);
          const evaluatee = Number(evaluateeId);
          const key = keyFor(evaluator, evaluatee);
          if (desired.has(key)) continue;
          desired.add(key);
          pairs.push({ evaluator, evaluatee });
        }
      }

      await apiClient.post('/department-evaluation-links/for-department/replace/', {
        department: deptId,
        pairs,
      });

      await loadLinks(); // refresh after save
      setToast({
        title: 'Relações de Avaliação',
        message: 'Relações de avaliação atualizadas com sucesso!',
        type: 'success',
      });
    } catch (e) {
      console.error(e);
      setToast({
        title: 'Relações de Avaliação',
        message: 'Erro ao gravar alterações.',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="container mt-4"><CSpinner size="sm" /> A carregar…</div>;
  }

  return (
    <div className="container mt-4">
      <ToastNotification toast={toast} setToast={setToast} />
      <div className="mb-3 d-flex justify-content-between align-items-center">
        <Link to={`/departamentos/${deptId}`}>&larr; Voltar</Link>
      </div>

      <CCard>
        <CCardHeader>
          <h4>Gestão de Relações de Avaliação</h4>
        </CCardHeader>
        <CCardBody>
          <CAccordion alwaysOpen>
            {members.map((u) => {
              const evalBy = evaluatedByRows.get(u.id) || [];
              const evalsDerived = evaluatesDerived.get(u.id) || [];
              return (
                <CAccordionItem itemKey={u.id} key={u.id}>
                  <CAccordionHeader>
                    <div className="d-flex flex-column">
                      <strong>{u.first_name} {u.last_name}</strong>
                    </div>
                  </CAccordionHeader>
                  <CAccordionBody>

                    {/* É avaliado por (editable when canEdit) */}
                    <CCard className="mb-3">
                      <CCardHeader className="d-flex justify-content-between align-items-center">
                        <div><strong>É avaliado por</strong></div>
                        {canEdit && (
                          <div className="d-flex gap-2">
                            <CButton size="sm" color="secondary" variant="outline" onClick={() => clearEvalBy(u.id)}>
                              <CIcon icon={cilX} className="me-1" /> Limpar
                            </CButton>
                            <CButton size="sm" color="primary" onClick={() => addRowEvalBy(u.id)}>
                              <CIcon icon={cilPlus} className="me-1" /> Adicionar Linha
                            </CButton>
                          </div>
                        )}
                      </CCardHeader>
                      <CCardBody>
                        <CTable small hover responsive>
                          <CTableBody>
                            {evalBy.map((val, idx) => (
                              <CTableRow key={`by-${u.id}-${idx}`}>
                                <CTableHeaderCell>
                                  <CFormSelect
                                    value={val}
                                    onChange={(e) => changeEvalBy(u.id, idx, e.target.value)}
                                    disabled={!canEdit}
                                  >
                                    <option value="">Selecione um utilizador</option>
                                    {evaluatorOptionsFor(u.id, val).map((x) => (
                                      <option key={x.id} value={x.id}>{labelOf(x)}</option>
                                    ))}
                                  </CFormSelect>
                                </CTableHeaderCell>
                                <CTableHeaderCell className="text-end">
                                  {canEdit && (
                                    <CButton size="sm" color="danger" variant="outline" onClick={() => removeRowEvalBy(u.id, idx)}>
                                      <CIcon icon={cilTrash} />
                                    </CButton>
                                  )}
                                </CTableHeaderCell>
                              </CTableRow>
                            ))}
                            {evalBy.length === 0 && (
                              <CTableRow>
                                <CTableHeaderCell colSpan={2} className="text-center text-body-secondary">
                                  Sem dados
                                </CTableHeaderCell>
                              </CTableRow>
                            )}
                          </CTableBody>
                        </CTable>
                      </CCardBody>
                    </CCard>

                    {/* Avalia (derived, view-only) */}
                    <CCard>
                      <CCardHeader>
                        <strong>Avalia</strong>
                      </CCardHeader>
                      <CCardBody>
                        <CTable small hover responsive>
                          <CTableBody>
                            {evalsDerived.length > 0 ? (
                              evalsDerived
                                .map((evaluateeId) => evaluateePool.find((m) => m.id === evaluateeId))
                                .filter(Boolean)
                                .map((m) => (
                                  <CTableRow key={`derived-${u.id}-${m.id}`}>
                                    <CTableHeaderCell>{labelOf(m)}</CTableHeaderCell>
                                  </CTableRow>
                                ))
                            ) : (
                              <CTableRow>
                                <CTableHeaderCell className="text-center text-body-secondary">
                                  Sem dados
                                </CTableHeaderCell>
                              </CTableRow>
                            )}
                          </CTableBody>
                        </CTable>
                      </CCardBody>
                    </CCard>

                  </CAccordionBody>
                </CAccordionItem>
              );
            })}
            {members.length === 0 && (
              <CAlert color="secondary" className="m-3">Sem membros neste departamento.</CAlert>
            )}
          </CAccordion>
        </CCardBody>
        <CCardFooter className="d-flex justify-content-end gap-2">
          <Link to={`/departamentos/${deptId}`} className="btn btn-secondary">Cancelar</Link>
          {canEdit && (
            <CButton color="success" className="text-white" onClick={onSave} disabled={saving}>
              {saving ? <CSpinner size="sm" /> : 'Guardar alterações'}
            </CButton>
          )}
        </CCardFooter>
      </CCard>
    </div>
  );
}
